import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const LineChart = ({ data, customData, customOptions }) => {
  const chartData = useMemo(() => {
    // Eğer özel veri sağlanmışsa direkt olarak kullan
    if (customData) {
      return customData;
    }
    
    // Eski format kontrolü (dizi değil, labels ve values içeren obje)
    if (data && !Array.isArray(data) && data.labels && data.values) {
      return {
        labels: data.labels,
        datasets: [
          {
            label: 'Öğrenci Sayısı',
            data: data.values,
            borderColor: 'rgba(99, 102, 241, 1)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointBackgroundColor: 'rgba(99, 102, 241, 1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 1,
            pointRadius: 4,
            pointHoverRadius: 6,
          }
        ]
      };
    }

    // Standart dizi kontrolü
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        labels: ['Veri Yok'],
        datasets: [
          {
            label: 'Öğrenci Sayısı',
            data: [0],
            borderColor: 'rgba(99, 102, 241, 0.5)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            tension: 0.3,
          }
        ]
      };
    }

    // Verileri işle ve tarihleri düzgün formata çevir
    try {
      // Verileri tarihe göre sırala (en yakın tarih en sonda)
      const sortedData = [...data].sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date : new Date(a.date);
        const dateB = b.date instanceof Date ? b.date : new Date(b.date);
        // Tarih geçerli değilse hata fırlatma, varsayılan sıralama kullan
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          return 0;
        }
        return dateA - dateB;
      });

      // Tarihleri ayın adına dönüştür
      const aylar = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
      const labels = sortedData.map(item => {
        let tarih;
        try {
          tarih = item.date instanceof Date ? item.date : new Date(item.date);
          // Tarih geçerli değilse varsayılan değer kullan
          if (isNaN(tarih.getTime())) return 'Geçersiz Tarih';
          return `${aylar[tarih.getMonth()]} ${tarih.getFullYear()}`;
        } catch (e) {
          console.error("Tarih dönüşüm hatası:", e, item.date);
          return 'Geçersiz Tarih';
        }
      });

      return {
        labels,
        datasets: [
          {
            label: 'Yeni Kayıt',
            data: sortedData.map(item => item.count || 0),
            borderColor: 'rgba(99, 102, 241, 1)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointBackgroundColor: 'rgba(99, 102, 241, 1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 1,
            pointRadius: 4,
            pointHoverRadius: 6,
          }
        ]
      };
    } catch (error) {
      console.error("LineChart veri işleme hatası:", error);
      // Hata durumunda varsayılan veri
      return {
        labels: ['Veri İşleme Hatası'],
        datasets: [
          {
            label: 'Hata',
            data: [0],
            borderColor: 'rgba(239, 68, 68, 0.5)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.3,
          }
        ]
      };
    }
  }, [data, customData]);

  const options = useMemo(() => {
    // Özel seçenekler varsa kullan
    if (customOptions) {
      return customOptions;
    }
    
    return {
      responsive: true,
      maintainAspectRatio: true,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            callback: function(value) {
              return value % 1 === 0 ? value : '';
            }
          },
          grid: {
            display: true,
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)',
          }
        },
        x: {
          grid: {
            display: false,
            drawBorder: false,
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            font: {
              size: 11
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'rgba(255, 255, 255, 1)',
          bodyColor: 'rgba(255, 255, 255, 1)',
          padding: 12,
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.raw} öğrenci`;
            }
          }
        }
      }
    };
  }, [customOptions]);

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 h-full">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
        Aylık Kayıt İstatistikleri
      </h3>
      <div className="h-[280px] flex items-center justify-center">
        {!data && !customData ? (
          <div className="text-center text-gray-500">
            <p>Kayıt istatistikleri verisi bulunamadı</p>
          </div>
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default LineChart;